import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { rc } from '../copyR3Cities'
import { cityLine, liveCities, LA_SLUG, useTrips, type City } from '../lib/cities'
import { Sheet } from '../ui/Sheet'
import { Spinner } from '../ui/Spinner'
import { Check } from '../ui/icons'
import { buttonClass } from '../ui/styles'

export const CITY_INPUT = 't-body min-h-11 w-full rounded-[12px] bg-surface px-3 ring-1 ring-inset ring-boundary/60'

/**
 * Round 3 Lane 6 (R-41): pick a city to browse. The live cities are listed (Los Angeles first); "Another city" types
 * one, which goes through Browse's own search: a city we have shows at once, one we don't starts the existing
 * getting-ready panel and the wish is saved (city {build} writes city_interest). `onPick(null)` = back to home.
 */
export function CitySwitcher({ open, onClose, current, home, onPick, mark = true, homeSlug = LA_SLUG }: {
  open: boolean; onClose: () => void; current: string | null; home: string | null; onPick: (where: string | null) => void
  /** false (Settings): no city is marked as the one showing */
  mark?: boolean
  /** the city their home is in (Los Angeles when none) */
  homeSlug?: string | null
}) {
  const [cities, setCities] = useState<City[] | null>(null)
  const [err, setErr] = useState(false)
  const [typed, setTyped] = useState('')
  const trips = useTrips()
  useEffect(() => {
    if (!open || cities) return
    let live = true
    liveCities().then((c) => { if (live) setCities(c) }).catch(() => { if (live) setErr(true) })
    return () => { live = false }
  }, [open, cities])
  const pick = (where: string | null) => { onPick(where); onClose() }
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const t = typed.trim()
    if (t.replace(/[^\p{L}\p{N}]/gu, '').length >= 2) { setTyped(''); pick(t) }
  }
  const hs = homeSlug ?? LA_SLUG
  const atHome = mark && (!current || current === hs)
  return (
    <Sheet open={open} onClose={onClose} label={rc('rc.sheet_title')} reviewState="city-switcher">
      <h2 className="t-control pr-10 text-[19px] leading-6 text-ink">{rc('rc.sheet_title')}</h2>
      <p className="t-meta mt-1 pr-10 text-ink-2">{rc('rc.sheet_sub')}</p>
      <div data-no-drag className="-mx-5 mt-3 max-h-[min(55dvh,520px)] touch-pan-y overflow-y-auto overscroll-contain px-5">
        {home && !atHome && (
          <button type="button" data-review-id="city-home" onClick={() => pick(null)}
            className="press flex min-h-[52px] w-full items-center rounded-[12px] px-3 text-left font-semibold text-accent-ink hover:bg-sunk">
            {rc('rc.home', { home })}
          </button>
        )}
        {!cities && !err && <p role="status" className="t-meta flex min-h-[52px] items-center gap-2 px-3 text-ink-2"><Spinner size={16} label={false} />{rc('rc.loading')}</p>}
        {err && <p role="alert" className="t-meta px-3 py-2 text-ink-2">{rc('rc.error')}</p>}
        {cities && (
          <ul aria-label={rc('rc.sheet_title')}>
            {cities.map((c) => {
              const here = mark && (c.slug === current || (atHome && c.slug === hs && !current))
              return (
                <li key={c.slug}>
                  <button type="button" data-review-id={`city:${c.slug}`} disabled={here} aria-current={here || undefined}
                    onClick={() => pick(c.slug === hs && home ? null : c.label)}
                    className="press flex min-h-[52px] w-full items-center justify-between gap-3 rounded-[12px] px-3 py-1.5 text-left hover:bg-sunk disabled:opacity-70">
                    <span className="min-w-0">
                      <span className="block text-[16px] font-semibold leading-5 text-ink">{cityLine(c)}</span>
                      {c.places > 0 && <span className="t-meta block text-ink-2">{rc('rc.places', { count: c.places.toLocaleString('en-US') })}</span>}
                    </span>
                    {here && <Check size={18} strokeWidth={2.75} className="shrink-0 text-ink-2" />}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
      <form onSubmit={submit} className="mt-4">
        <label htmlFor="city-other" className="t-label text-ink-2">{rc('rc.other_label')}</label>
        <div className="mt-1 flex gap-2">
          <input id="city-other" data-review-id="city-other" value={typed} onChange={(e) => setTyped(e.target.value)} maxLength={80} autoComplete="off"
            placeholder={rc('rc.other_placeholder')} className={CITY_INPUT} />
          <button type="submit" className={buttonClass('secondary', 'md', 'shrink-0')}>{rc('rc.other_go')}</button>
        </div>
      </form>
      {trips && <Link to="/trip/new" onClick={onClose} data-review-id="city-trip" className="press t-meta mt-3 inline-flex min-h-11 items-center font-semibold text-accent-ink underline underline-offset-4">{rc('rc.trip_new')}</Link>}
    </Sheet>
  )
}

/** The small "Change city" control: under Browse's headline on a wide screen, after the first row below that. */
export function ChangeCity({ onClick, className = 'inline-flex' }: { onClick: () => void; className?: string }) {
  return (
    <button type="button" data-review-id="change-city" onClick={onClick}
      className={`press t-meta min-h-11 items-center font-semibold text-accent-ink underline underline-offset-4 ${className}`}>
      {rc('rc.change')}
    </button>
  )
}
